import { alpha, Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { formatCurrency, useLocale } from '@repo/i18n';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { useCartCheckoutContext } from '../CartCheckoutContext';
import CheckoutSummary, { useSummaryFeatures } from '../../CheckoutSummary';
import useCartItemsForSummary from 'src/hooks/domain/useCartItemsForSummary';
import { ButtonDisplay } from './ButtonDisplay';
import { useWidgetEventEffect } from 'src/hooks/domain/events/useWidgetEventEffect';
import { useAtom } from 'ximple';
import { cartAtom } from 'src/state/cart/cartAtom';
import {
    OctoBooking,
    OctoBookingUnitItem,
    OctoBookingUpdateRequest,
    OctoExtra,
    OctoPrice,
    OctoProductUnit,
} from '@repo/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Currency } from '@repo/types';
import { tzdate } from '@repo/tzdate';
import { getOctoOrder, updateOctoBooking } from '@repo/widget-utils/services/fetchers/octo-api';
import { octoOrderAtom, setOctoOrder } from 'src/state/octo-cart/octoOrderAtom';
import { ExtraProductCard } from 'src/components/domain/extra-product-card/ExtraProductCard';
import { QuantityInputItem } from '@repo/widget-utils/booking/domains/shared/types';

// --- Types for extra selection state ---

type ExtraSelectionKey = `${string}-${string}`; // unitItemUuid-extraId

type ExtraSelectionsState = Map<ExtraSelectionKey, number>;

function makeSelectionKey(unitItemUuid: string, extraId: string): ExtraSelectionKey {
    return `${unitItemUuid}-${extraId}`;
}

type ExtraSelectionHandlers = {
    getQuantity: (unitItemUuid: string, extraId: string) => number;
    setQuantity: (unitItemUuid: string, extraId: string, quantity: number) => void;
    getServerQuantity: (unitItemUuid: string, extraId: string) => number;
    hasChangesForExtra: (extraId: string, unitItems: OctoBookingUnitItem[]) => boolean;
    clearSelectionsForExtra: (extraId: string, unitItems: OctoBookingUnitItem[]) => void;
};

type OctoExtrasViewProps = {
    onNextClicked: () => void;
};

export function OctoExtrasView({ onNextClicked }: OctoExtrasViewProps) {
    const { t, locale } = useLocale();
    const theme = useTheme();
    const customizations = useCustomizations();
    const [cartItems] = useAtom(cartAtom);
    const { activeTab, largeScreen, isMakingReservation, octoOrderInCart } =
        useCartCheckoutContext();

    const cartItemsForSummary = useCartItemsForSummary();

    const summaryFeatures = useSummaryFeatures(
        true, // showShare
        true, // enableCartClearing
        true, // enableItemEditing
        activeTab,
        cartItemsForSummary.isWaitingListMode,
    );

    useWidgetEventEffect(() => ({
        eventType: 'checkoutStep',
        checkoutStep: 'Extras', // Use 'Extras' for analytics - same user-facing step
        cartItems: cartItems,
    }));

    // Find bookings that have booked units with extras
    const bookingsWithExtras = useMemo(() => {
        if (!octoOrderInCart?.bookings) return [];

        return octoOrderInCart.bookings
            .map((booking) => {
                const unitItems = booking.unitItems ?? [];

                // Check if any booked unit has extras
                const hasAnyExtras = unitItems.some((unitItem) => {
                    const unit = booking.option?.units?.find((u) => u.id === unitItem.unitId);
                    return unit?.extras && unit.extras.length > 0;
                });

                if (!hasAnyExtras) return null;

                return { booking, unitItems };
            })
            .filter(Boolean) as Array<{
            booking: OctoBooking;
            unitItems: OctoBookingUnitItem[];
        }>;
    }, [octoOrderInCart]);

    const hasExtras = bookingsWithExtras.length > 0;

    // --- Local state for pending extra selections ---
    const [pendingSelections, setPendingSelections] = useState<ExtraSelectionsState>(new Map());

    // Get quantity from server state (existing extraItems on unitItem)
    const getServerQuantity = useCallback(
        (unitItemUuid: string, extraId: string): number => {
            for (const { unitItems } of bookingsWithExtras) {
                const unitItem = unitItems.find((ui) => ui.uuid === unitItemUuid);
                if (unitItem) {
                    return unitItem.extraItems?.filter((ei) => ei.extraId === extraId).length ?? 0;
                }
            }
            return 0;
        },
        [bookingsWithExtras],
    );

    // Get quantity from local pending state, falling back to server state
    const getQuantity = useCallback(
        (unitItemUuid: string, extraId: string): number => {
            const key = makeSelectionKey(unitItemUuid, extraId);
            if (pendingSelections.has(key)) {
                return pendingSelections.get(key)!;
            }
            return getServerQuantity(unitItemUuid, extraId);
        },
        [pendingSelections, getServerQuantity],
    );

    // Set quantity in local pending state
    const setQuantity = useCallback((unitItemUuid: string, extraId: string, quantity: number) => {
        setPendingSelections((prev) => {
            const next = new Map(prev);
            const key = makeSelectionKey(unitItemUuid, extraId);
            next.set(key, quantity);
            return next;
        });
    }, []);

    // Check if an extra has pending changes for any of its unit items
    const hasChangesForExtra = useCallback(
        (extraId: string, unitItems: OctoBookingUnitItem[]): boolean => {
            return unitItems.some((unitItem) => {
                const key = makeSelectionKey(unitItem.uuid, extraId);
                if (!pendingSelections.has(key)) return false;
                const pendingQty = pendingSelections.get(key)!;
                const serverQty = getServerQuantity(unitItem.uuid, extraId);
                return pendingQty !== serverQty;
            });
        },
        [pendingSelections, getServerQuantity],
    );

    // Clear pending selections for an extra (after successful API update)
    const clearSelectionsForExtra = useCallback(
        (extraId: string, unitItems: OctoBookingUnitItem[]) => {
            setPendingSelections((prev) => {
                const next = new Map(prev);
                for (const unitItem of unitItems) {
                    const key = makeSelectionKey(unitItem.uuid, extraId);
                    next.delete(key);
                }
                return next;
            });
        },
        [],
    );

    const selectionHandlers: ExtraSelectionHandlers = useMemo(
        () => ({
            getQuantity,
            setQuantity,
            getServerQuantity,
            hasChangesForExtra,
            clearSelectionsForExtra,
        }),
        [getQuantity, setQuantity, getServerQuantity, hasChangesForExtra, clearSelectionsForExtra],
    );

    // Skip to next tab if no extras available
    useEffect(() => {
        if (!hasExtras && activeTab === 'octoextras') {
            onNextClicked();
        }
    }, [hasExtras, activeTab, onNextClicked]);

    if (!hasExtras) {
        return null;
    }

    return (
        <Stack
            justifyContent={largeScreen ? 'space-between' : 'center'}
            alignItems={largeScreen ? 'flex-start' : 'center'}
            direction={largeScreen ? 'row' : 'column-reverse'}
            gap={3}
            position="relative"
            width="100%"
        >
            {!largeScreen && (
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    width="100%"
                    sx={{
                        maxWidth: '440px',
                        boxSizing: 'border-box',
                        [theme.breakpoints.down('md')]: {
                            marginBottom: theme.spacing(3),
                            maxWidth: '100%',
                            px: theme.spacing(2),
                        },
                    }}
                >
                    <Button
                        color="primary"
                        variant={customizations.primaryButtonStyle}
                        disabled={isMakingReservation}
                        onClick={onNextClicked}
                    >
                        <ButtonDisplay
                            isMakingReservation={isMakingReservation}
                            buttonText={t.continue_travelers}
                        />
                    </Button>
                </Stack>
            )}
            <Stack mt={4} mb={4} gap={5} width="100%">
                {bookingsWithExtras.map(({ booking, unitItems }) => (
                    <OctoBookingExtrasGroup
                        key={booking.id}
                        booking={booking}
                        unitItems={unitItems}
                        locale={locale}
                        selectionHandlers={selectionHandlers}
                    />
                ))}
            </Stack>
            <Box minWidth={{ xs: 'auto', md: 400 }}>
                <CheckoutSummary
                    cartItems={cartItemsForSummary.itemsToShow}
                    hideGoPay={!largeScreen}
                    isMakingReservation={isMakingReservation}
                    goPayClicked={onNextClicked}
                    nextButtonText={t.continue_travelers}
                    summaryFeatures={summaryFeatures}
                />
            </Box>
        </Stack>
    );
}

// --- Sub-components ---

type UnitItemWithUnit = {
    unitItem: OctoBookingUnitItem;
    unit: OctoProductUnit;
};

type OctoBookingExtrasGroupProps = {
    booking: OctoBooking;
    unitItems: OctoBookingUnitItem[];
    locale: string;
    selectionHandlers: ExtraSelectionHandlers;
};

function OctoBookingExtrasGroup({
    booking,
    unitItems,
    locale,
    selectionHandlers,
}: OctoBookingExtrasGroupProps) {
    const theme = useTheme();

    // Parse the availability start time
    const startDateTime = booking.availability?.localDateTimeStart
        ? tzdate(booking.availability.localDateTimeStart)
        : null;

    // Collect all unique extras from all booked units
    const extrasWithUnitItems = useMemo(() => {
        const extraMap = new Map<
            string,
            {
                extra: OctoExtra;
                unitItemsWithUnit: UnitItemWithUnit[];
            }
        >();

        for (const unitItem of unitItems) {
            const unit = booking.option?.units?.find((u) => u.id === unitItem.unitId);
            if (!unit?.extras) continue;

            for (const extra of unit.extras) {
                if (!extraMap.has(extra.id)) {
                    extraMap.set(extra.id, { extra, unitItemsWithUnit: [] });
                }
                extraMap.get(extra.id)!.unitItemsWithUnit.push({ unitItem, unit });
            }
        }

        return Array.from(extraMap.values());
    }, [unitItems, booking.option?.units]);

    return (
        <Stack gap={2}>
            <Box>
                <Typography variant="h2" lineHeight={1} mb={2}>
                    {booking.product?.title}
                </Typography>
                {startDateTime && (
                    <Typography
                        mb={2}
                        fontSize={16}
                        sx={{
                            borderBottom: `1px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
                        }}
                    >
                        {startDateTime.format('LL')}, {startDateTime.format('LT')}
                    </Typography>
                )}
            </Box>
            <Stack spacing={3}>
                {extrasWithUnitItems.map(({ extra, unitItemsWithUnit }) => (
                    <OctoExtraCard
                        key={extra.id}
                        extra={extra}
                        unitItemsWithUnit={unitItemsWithUnit}
                        booking={booking}
                        locale={locale}
                        selectionHandlers={selectionHandlers}
                    />
                ))}
            </Stack>
        </Stack>
    );
}

type OctoExtraCardProps = {
    extra: OctoExtra;
    unitItemsWithUnit: UnitItemWithUnit[];
    booking: OctoBooking;
    locale: string;
    selectionHandlers: ExtraSelectionHandlers;
};

function OctoExtraCard({
    extra,
    unitItemsWithUnit,
    booking,
    locale,
    selectionHandlers,
}: OctoExtraCardProps) {
    const { t } = useLocale();
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { getQuantity, setQuantity, getServerQuantity, hasChangesForExtra, clearSelectionsForExtra } =
        selectionHandlers;

    // Get price from extra.pricing (array of OctoPrice)
    const price = extra.pricing?.[0] ?? extra.pricingFrom?.[0];
    const formattedPrice = price ? formatOctoPrice(price, locale) : undefined;

    const unitItems = unitItemsWithUnit.map((x) => x.unitItem);
    const hasChanges = hasChangesForExtra(extra.id, unitItems);

    // Check if any extras of this type are already on the server (already added to cart)
    const hasExistingExtras = unitItems.some(
        (unitItem) => getServerQuantity(unitItem.uuid, extra.id) > 0,
    );

    const handleAddToCart = async () => {
        setIsUpdating(true);
        setError(null);

        try {
            // Build the update request body
            // We need to include ALL unitItems from the booking, updating only the extraItems
            const updateRequest: OctoBookingUpdateRequest = {
                productId: booking.productId,
                optionId: booking.optionId,
                availabilityId: booking.availabilityId,
                unitItems: (booking.unitItems ?? []).map((existingUnitItem) => {
                    // For unitItems that match the ones we're updating, apply pending selections
                    const isPendingUnit = unitItems.some((ui) => ui.uuid === existingUnitItem.uuid);

                    // Build extraItems array for this unitItem
                    // Start with existing extraItems (excluding the extra we're updating if this is a pending unit)
                    const existingExtraItems = existingUnitItem.extraItems ?? [];
                    let newExtraItems: Array<{ extraId: string }>;

                    if (isPendingUnit) {
                        // For units we're updating: keep other extras, replace this extra's quantity
                        const otherExtras = existingExtraItems
                            .filter((ei) => ei.extraId !== extra.id)
                            .map((ei) => ({ extraId: ei.extraId }));

                        // Add this extra based on pending selection quantity
                        const pendingQuantity = getQuantity(existingUnitItem.uuid, extra.id);
                        const thisExtraItems = Array(pendingQuantity).fill({
                            extraId: extra.id,
                        });

                        newExtraItems = [...otherExtras, ...thisExtraItems];
                    } else {
                        // For other units: keep existing extraItems as-is
                        newExtraItems = existingExtraItems.map((ei) => ({
                            extraId: ei.extraId,
                        }));
                    }

                    return {
                        uuid: existingUnitItem.uuid,
                        unitId: existingUnitItem.unitId,
                        extraItems: newExtraItems,
                    };
                }),
            };

            // Call the API
            await updateOctoBooking(booking.id, updateRequest);

            // Refresh the order from the server
            const updatedOrder = await getOctoOrder(booking.orderId);
            octoOrderAtom.update(setOctoOrder(updatedOrder));

            // Clear pending selections for this extra
            clearSelectionsForExtra(extra.id, unitItems);
        } catch (err) {
            console.error('Failed to update booking with extras:', err);
            setError(t.an_error_occurred_when_submitting_your_request);
            // State rollback happens automatically since we didn't clear pending selections
        } finally {
            setIsUpdating(false);
        }
    };

    // Build quantity input items for the dumb component
    // Count occurrences of each unit type for labeling (e.g., "Adult 1", "Adult 2")
    const unitCounters = new Map<string, number>();
    const quantityInputs: QuantityInputItem[] = unitItemsWithUnit.map(({ unitItem, unit }) => {
        const count = (unitCounters.get(unit.id) ?? 0) + 1;
        unitCounters.set(unit.id, count);

        const quantity = getQuantity(unitItem.uuid, extra.id);
        const minQty = extra.restrictions.minQuantity;
        const maxQty = extra.restrictions.maxQuantity;

        // Show number suffix only if there are multiple of this unit type
        const totalOfThisUnit = unitItemsWithUnit.filter((x) => x.unit.id === unit.id).length;
        const label = totalOfThisUnit > 1 ? `${unit.title} ${count}` : unit.title;

        return {
            id: unitItem.uuid,
            label,
            value: quantity,
            min: minQty,
            max: maxQty ?? Number.MAX_SAFE_INTEGER,
            disabled: isUpdating,
        };
    });

    // Handle quantity change - update local pending state
    const handleQuantityChange = useCallback(
        (unitItemUuid: string, newQty: number) => {
            setQuantity(unitItemUuid, extra.id, newQty);
        },
        [setQuantity, extra.id],
    );

    // Get cover image from extra.media if available
    const coverImage = extra.media?.find((m) => m.rel === 'COVER') ?? extra.media?.[0];

    // Button says "Bestill nå" (book_now) when no extras added, "Oppdater" (update) when some exist
    const buttonLabel = hasExistingExtras ? t.update : t.book_now;

    return (
        <ExtraProductCard
            title={extra.title}
            description={extra.description}
            price={formattedPrice}
            imageUrl={coverImage?.src}
            imageAlt={coverImage?.title ?? extra.title}
            quantityInputs={quantityInputs}
            onQuantityChange={handleQuantityChange}
            action={{
                label: buttonLabel,
                onClick: handleAddToCart,
                disabled: !hasChanges,
                loading: isUpdating,
            }}
            error={error ?? undefined}
        />
    );
}

// --- Helper functions ---

function formatOctoPrice(price: OctoPrice, locale: string): string {
    const amount = price.retail / Math.pow(10, price.currencyPrecision);
    const currency: Currency = {
        currency: price.currency,
        currencyDisplay: 'narrowSymbol',
    };
    return formatCurrency(locale, currency, amount);
}
